import { useLocation, useNavigate } from 'react-router-dom'
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Toolbar,
    Box,
    Divider,
    Typography,
} from '@mui/material'
import {
    Dashboard,
    Inbox,
    Business,
    Settings,
    Archive,
    People,
    AssignmentTurnedIn,
    PersonAdd,
    QrCodeScanner,
    Email,
    Groups,
    WhatsApp,
    Forum,
    QuestionAnswer,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useTranslation } from '@i18n'

const DRAWER_WIDTH = 260

const Sidebar = ({ open, onClose, variant = 'permanent' }) => {
    const location = useLocation()
    const navigate = useNavigate()
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { sidebarCollapsed } = useUIStore()

    const isRTL = language === 'ar'
    const isAdmin = user?.user_type === 'admin'
    const isFormManager = user?.user_type === 'form_manager'
    const isDashboardUser = ['admin', 'dashboard'].includes(user?.user_type)
    const isDepartmentUser = user?.user_type === 'department'
    const isDepartmentLeader = isDepartmentUser && user?.is_department_leader

    const menuItems = [
        {
            path: '/dashboard',
            icon: <Dashboard />,
            label: t('nav.dashboard'),
            show: isAdmin || isFormManager || isDashboardUser || isDepartmentUser, // Department users can see dashboard
        },
        {
            path: '/submissions',
            icon: <Inbox />,
            label: t('nav.submissions'),
            show: isAdmin || isFormManager || isDashboardUser || isDepartmentUser, // Department users see navigated submissions
        },
        {
            path: '/direct-submissions',
            icon: <PersonAdd />,
            label: isRTL ? 'الإدخال المباشر' : 'Direct Submissions',
            show: isAdmin || isFormManager || (isDashboardUser && user?.enable_direct_submissions), // Show for admin, form managers, or dashboard users with direct submissions enabled
        },
        {
            path: '/pending-approvals',
            icon: <AssignmentTurnedIn />,
            label: isRTL ? 'الموافقات المعلقة' : 'Pending Approvals',
            show: isDepartmentUser || isDashboardUser || isFormManager, // Form managers CAN see this
        },
        {
            path: '/forms',
            icon: <Business />,
            label: t('nav.forms'),
            show: isAdmin || isFormManager, // Form managers can see forms (filtered)
        },
        {
            path: '/departments',
            icon: <People />,
            label: t('nav.departments'),
            show: isAdmin, // Only admin can manage departments
        },
        {
            path: '/registrations',
            icon: <PersonAdd />,
            label: isRTL ? 'التسجيلات' : 'Registrations',
            show: isAdmin, // Only admin can manage user registrations
        },
        {
            path: '/scanner',
            icon: <QrCodeScanner />,
            label: isRTL ? 'ماسح QR' : 'QR Scanner',
            show: isAdmin || isFormManager, // Admin and form managers can scan QR codes
        },
        {
            path: '/emails',
            icon: <Email />,
            label: isRTL ? 'إدارة البريد الإلكتروني' : 'Email Management',
            show: isAdmin, // Only admin can send global emails
        },
        {
            path: '/whatsapp',
            icon: <WhatsApp sx={{ color: '#25D366' }} />,
            label: isRTL ? 'رسائل WhatsApp' : 'WhatsApp',
            // Admin always sees it, form managers and dashboard users if they have whatsapp enabled forms
            show: isAdmin || isFormManager || (isDashboardUser && user?.enable_whatsapp),
        },
        {
            path: '/live-chat',
            icon: <Forum />,
            label: isRTL ? 'المحادثة المباشرة' : 'Live Chat',
            show: isAdmin || isFormManager || (isDashboardUser && user?.enable_live_chat),
        },
        {
            path: '/chat-submissions',
            icon: <QuestionAnswer />,
            label: isRTL ? 'المحادثات المحفوظة' : 'Chat Submissions',
            show: isAdmin || isFormManager || (isDashboardUser && user?.enable_live_chat),
        },
        {
            path: '/my-department',
            icon: <Business />,
            label: isRTL ? 'قسمي' : 'My Department',
            show: isDepartmentUser, // All department users can see their department
        },
        {
            path: '/department-users',
            icon: <Groups />,
            label: isRTL ? 'فريقي' : 'My Team',
            show: isDepartmentLeader, // Only department leaders can manage their team
        },
        {
            path: '/settings',
            icon: <Settings />,
            label: t('nav.settings'),
            show: isAdmin, // Only admin can access settings
        },
    ]

    const handleNavigation = (path) => {
        navigate(path)
        if (variant === 'temporary') {
            onClose()
        }
    }

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar />

            <List sx={{ flexGrow: 1, px: 1 }}>
                {menuItems
                    .filter((item) => item.show)
                    .map((item) => {
                        const isActive = location.pathname === item.path
                        return (
                            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    sx={{
                                        borderRadius: 2,
                                        bgcolor: isActive ? 'primary.main' : 'transparent',
                                        color: isActive ? 'primary.contrastText' : 'text.primary',
                                        '&:hover': {
                                            bgcolor: isActive ? 'primary.dark' : 'action.hover',
                                        },
                                        '& .MuiListItemIcon-root': {
                                            color: isActive ? 'primary.contrastText' : 'text.secondary',
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: isActive ? 600 : 400,
                                            textAlign: isRTL ? 'right' : 'left',
                                        }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        )
                    })}
            </List>

            <Divider />

            <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                    {isRTL ? (useUIStore.getState().themeSettings?.systemNameAr || 'وزارة العدل') : (useUIStore.getState().themeSettings?.systemNameEn || 'Ministry of Justice')}
                </Typography>
                <Typography variant="caption" display="block" color="text.disabled">
                    v1.0.0
                </Typography>
            </Box>
        </Box>
    )

    if (variant === 'temporary') {
        return (
            <Drawer
                variant="temporary"
                anchor={isRTL ? 'right' : 'left'}
                open={open}
                onClose={onClose}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxSizing: 'border-box',
                        width: DRAWER_WIDTH,
                    },
                }}
            >
                {drawerContent}
            </Drawer>
        )
    }

    return (
        <Drawer
            variant="permanent"
            anchor={isRTL ? 'right' : 'left'}
            sx={{
                width: sidebarCollapsed ? 72 : DRAWER_WIDTH,
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
                '& .MuiDrawer-paper': {
                    width: sidebarCollapsed ? 72 : DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    borderRight: isRTL ? 'none' : '1px solid',
                    borderLeft: isRTL ? '1px solid' : 'none',
                    borderColor: 'divider',
                    transition: 'width 0.2s ease',
                    right: isRTL ? 0 : 'auto',
                    left: isRTL ? 'auto' : 0,
                },
            }}
        >
            {drawerContent}
        </Drawer>
    )
}

export default Sidebar

